import palette from 'lib/styles/palette';
import styled, { css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import usePlaceList from 'hooks/place/usePlaceList';
import Loading from 'components/common/Loading';
import ErrorNoti from 'components/common/ErrorNoti';
import Pagination from 'components/common/Pagination';
import { useSelector } from 'react-redux';
import { SK_STORE_ID_LIST } from 'constants';
import { DGTAS_STORE_ID_LIST } from 'constants';

const Row = ({ store, registered }) => {
  const navigate = useNavigate();

  const onMove = () => {
    registered && navigate(`${store.id}`);
  };

  return (
    <RowBlock registered={registered} onClick={onMove}>
      <div className="createdAt">{store.createdAt}</div>
      <div className="company">{store.company}</div>
      <div className="place">{store.name}</div>
      {!registered && (
        <button onClick={() => navigate(`${store.id}/creator`)}>
          도면 등록
        </button>
      )}
    </RowBlock>
  );
};

const RowBlock = styled.div`
  ${props =>
    props.registered &&
    css`
      cursor: pointer;
    `}

  display: flex;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  button {
    border-radius: 8px;
    border: none;
    padding: 8px 12px;
    font-size: 10px;
    color: #fff;
    cursor: pointer;
    background: ${palette.theme[3]};
    :hover {
      background: ${palette.theme[2]};
    }
  }

  .createdAt {
    width: 30%;
  }

  .company {
    width: 30%;
  }

  .place {
    width: 30%;
  }
`;

const PlaceList = () => {
  const { partner } = useSelector(({ auth }) => auth);

  const {
    isLoading,
    error,
    addStoreListPage,
    storeListPage,
    addStoreListSearch,
    storeListPageSearch,
    addStoreList,
    storeList,
    onPaginateAddStore,
    onPaginateStore,
    onSearch,
  } = usePlaceList();

  const addFilteredStoreList =
    partner === 'seedn'
      ? addStoreList?.rows
      : partner === 'sk'
      ? addStoreList?.rows.filter(row => SK_STORE_ID_LIST.includes(row.id))
      : partner === 'dgtas'
      ? addStoreList?.rows.filter(row => DGTAS_STORE_ID_LIST.includes(row.id))
      : [];

  const filteredStoreList =
    partner === 'seedn'
      ? storeList?.rows
      : partner === 'sk'
      ? storeList?.rows.filter(row => SK_STORE_ID_LIST.includes(row.id))
      : partner === 'dgtas'
      ? storeList?.rows.filter(row => DGTAS_STORE_ID_LIST.includes(row.id))
      : [];

  if (isLoading) return <Loading />;

  if (error?.response.status === 500) return <ErrorNoti />;

  return (
    <PlaceListBlock>
      <Header>
        <div className="title">장소 등록 / 조회</div>
      </Header>
      <Wrapper>
        <div className="header">
          <div className="title">장소 등록</div>
          <StyledInput
            type="text"
            placeholder="회사명, 장소명을 입력해 주세요"
            name="addStoreListSearch"
            value={addStoreListSearch}
            onChange={onSearch}
          />
        </div>
        <div className="table">
          <div className="table-header">
            <div className="createdAt">등록일</div>
            <div className="company">계약 회사</div>
            <div className="place">설치 장소</div>
          </div>
          {addFilteredStoreList?.length === 0 ? (
            <div className="place-list-no-list">목록이 없습니다</div>
          ) : (
            <>
              {addFilteredStoreList?.map(store => (
                <Row key={store.id} store={store} />
              ))}
              <Pagination
                totalPage={Math.ceil(addStoreList?.count / 5)}
                currentPage={addStoreListPage}
                setCurrentPage={onPaginateAddStore}
              />
            </>
          )}
        </div>
      </Wrapper>
      <Wrapper>
        <div className="header">
          <div className="title">장소 조회</div>
          <StyledInput
            type="text"
            placeholder="회사명, 장소명을 입력해 주세요"
            name="storeListPageSearch"
            value={storeListPageSearch}
            onChange={onSearch}
          />
        </div>
        <div className="table">
          <div className="table-header">
            <div className="createdAt">등록일</div>
            <div className="company">계약 회사</div>
            <div className="place">설치 장소</div>
          </div>
          {filteredStoreList?.length === 0 ? (
            <div className="place-list-no-list">목록이 없습니다</div>
          ) : (
            <>
              {filteredStoreList?.map(store => (
                <Row key={store.id} store={store} registered />
              ))}
              <Pagination
                totalPage={Math.ceil(storeList?.count / 5)}
                currentPage={storeListPage}
                setCurrentPage={onPaginateStore}
              />
            </>
          )}
        </div>
      </Wrapper>
    </PlaceListBlock>
  );
};

export default PlaceList;

const PlaceListBlock = styled.div`
  width: 1080px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding-bottom: 30px;
`;

const Header = styled.div`
  margin-top: 2rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .title {
    font-size: 22px;
    font-weight: bold;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .title {
    font-size: 18px;
    font-weight: bold;
  }

  & + & {
    margin-top: 2rem;
  }

  .table {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    border: solid 1px rgba(0, 0, 0, 0.08);
    border-radius: 20px;
    box-shadow: 0 0 50px 0 rgba(0, 0, 0, 0.08);
    padding: 0 60px;
    text-align: center;
  }

  .table-header {
    display: flex;
    font-weight: bold;
    display: flex;
    padding: 20px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);

    .createdAt {
      width: 30%;
    }

    .company {
      width: 30%;
    }

    .place {
      width: 30%;
    }
  }

  .place-list-no-list {
    padding: 2rem;
    font-weight: 500;
  }
`;

const StyledInput = styled.input`
  width: 600px;
  height: 40px;
  border: solid 1px #dfdfdf;
  border-radius: 11px;
  background: #fff;
  padding: 15px;
  font-size: 16px;
  &::placeholder {
    color: #c6c6c6;
  }
`;
